import { motion } from 'framer-motion'
import crownbisiShot from '../assets/testimonials/crownbisi.webp'
import everythingSuitsShot from '../assets/testimonials/everything-suits.webp'
import candleBrandShot from '../assets/testimonials/candle-brand.webp'
import myraShot from '../assets/testimonials/myra.webp'
import crownbisiLogo from '../assets/case-studies/crownbisi-logo.webp'
import { duration, stagger } from '../lib/motion'

const LOGOS = [
  { image: crownbisiLogo, name: 'Crownbisi Tech Ventures', desc: 'Internet & devices retail', bg: '#000000' },
  { letterMark: 'SUITS', name: 'Everything Suits', desc: 'Made-to-measure tailoring', bg: '#1a1a1a' },
]

const TESTIMONIALS = [
  {
    image: crownbisiShot,
    name: 'Crownbisi Tech Ventures',
    context: 'First reaction to seeing the homepage concept',
    width: 715,
    height: 593,
  },
  {
    image: everythingSuitsShot,
    name: 'Everything Suits',
    context: 'Reaction to the completed order system going live',
    width: 720,
    height: 228,
  },
  {
    image: candleBrandShot,
    name: 'Scented candle brand client',
    context: 'First reaction to the concept, mid-conversation about the layout direction',
    width: 720,
    height: 601,
  },
  {
    image: myraShot,
    name: 'Myra',
    context: 'Reaction to the homepage mockup',
    width: 720,
    height: 478,
  },
]

// Real Google reviews from the Better Website Co. Google Business Profile
// (verified listing) — each rendered as its own standalone card, not
// merged or summarized. Names, ratings, and quotes copied exactly as they
// appear on the live, verified profile.
const GOOGLE_REVIEWS = [
  {
    name: 'Paul Davies',
    rating: 5,
    text: "They built a stunning, easy-to-use website for our lodging business. The project was finished on time, and communication was great. Highly recommend and would love to work with next time!!!!",
  },
  {
    name: 'Femi-Oladeji Makanjuola',
    rating: 5,
    text: 'Out of thin air, a website with proper details of my business manifested, with their help.',
  },
  {
    name: 'shota sprunger',
    rating: 5,
    text: 'Great to work with and exceeds the expectations! Very professional and fast',
  },
]

function StarRow({ count }) {
  return (
    <div className="flex gap-0.5 mb-4" aria-label={`${count} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <span key={i} style={{ color: i < count ? 'var(--accent)' : 'var(--border-strong)' }}>
          ★
        </span>
      ))}
    </div>
  )
}

function GoogleReviewCard({ review, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: duration.base, delay: index * stagger.base }}
      className="rounded-2xl border p-7 flex flex-col"
      style={{ borderColor: 'var(--border)', background: 'var(--card)' }}
    >
      <StarRow count={review.rating} />
      <p className="text-base leading-relaxed flex-1" style={{ color: 'var(--text-primary)' }}>
        “{review.text}”
      </p>
      <div className="mt-5 flex items-start justify-between gap-3">
        <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
          {review.name}
        </p>
        <span className="text-xs font-mono whitespace-nowrap shrink-0" style={{ color: 'var(--text-muted)' }}>
          Google review
        </span>
      </div>
    </motion.div>
  )
}

export default function Testimonials() {
  return (
    <section id="testimonials" className="py-24 border-t" style={{ borderColor: 'var(--border)' }}>
      <div className="container-bw">
        <div className="max-w-xl mb-14">
          <p className="eyebrow mb-4">In their words</p>
          <h2 className="font-display text-3xl md:text-4xl" style={{ color: 'var(--text-primary)' }}>
            Testimonials
          </h2>
          <p className="mt-4 text-sm" style={{ color: 'var(--text-muted)' }}>
            Real client messages. Nothing staged, nothing invented.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
          {GOOGLE_REVIEWS.map((review, i) => (
            <GoogleReviewCard key={review.name} review={review} index={i} />
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-4 mb-14">
          {LOGOS.map((l, i) => (
            <motion.div
              key={l.name}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: duration.base, delay: i * stagger.tight }}
              className="flex items-center gap-3 rounded-xl border px-5 py-4"
              style={{ borderColor: 'var(--border)', background: 'var(--card)' }}
            >
              <div
                className="h-10 w-16 rounded-md flex items-center justify-center overflow-hidden shrink-0"
                style={{ background: l.bg }}
              >
                {l.image ? (
                  <img src={l.image} alt={l.name} className="h-6 w-auto object-contain" />
                ) : (
                  <span className="text-[9px] font-semibold tracking-widest text-white">{l.letterMark}</span>
                )}
              </div>
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                  {l.name}
                </p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  {l.desc}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl">
          {TESTIMONIALS.map((t, i) => (
            <motion.div
              key={t.name}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: duration.base, delay: i * stagger.base }}
              className="rounded-2xl border overflow-hidden flex flex-col"
              style={{ borderColor: 'var(--border)', background: 'var(--card)' }}
            >
              <div
                className="flex items-center justify-center p-5"
                style={{ background: '#0a0f0d' }}
              >
                <img
                  src={t.image}
                  alt={`Message from ${t.name}`}
                  width={t.width}
                  height={t.height}
                  className="w-full h-auto rounded-2xl"
                  style={{ maxHeight: 280, objectFit: 'contain' }}
                />
              </div>
              <div className="p-6">
                <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                  {t.name}
                </p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                  {t.context}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
